import { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  supabase,
  TokenManager,
  type StoredTokenData,
} from "../supabase/supabaseClient";
import type { User } from "@supabase/supabase-js";
import { useAuth } from "@/hooks/useAuth";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";
import { toast, Toaster } from "react-hot-toast";
import { Map as MapLibreMap, type StyleSpecification } from "maplibre-gl";
import { getDisplayVersion } from "@/lib/version";
import "maplibre-gl/dist/maplibre-gl.css";

// Helper function to get the last active route for post-login navigation
function getLastActiveRoute(): string {
  try {
    const lastRoute = localStorage.getItem("taskr-last-active-route");
    // Valid navigation routes
    const validRoutes = [
      "/map",
      "/dashboard",
      "/asset-management",
      "/reporting",
      "/bulk-assignments",
    ];
    return lastRoute && validRoutes.includes(lastRoute) ? lastRoute : "/map";
  } catch (error) {
    console.warn("Error reading last route from localStorage:", error);
    return "/map";
  }
}

interface AuthProps {
  isPasswordResetFlow?: boolean;
  onPasswordResetComplete?: () => void;
  isInviteFlow?: boolean;
  onInviteComplete?: () => void;
}

export function Auth({
  isPasswordResetFlow: propIsPasswordResetFlow = false,
  onPasswordResetComplete,
  isInviteFlow: propIsInviteFlow = false,
  onInviteComplete,
}: AuthProps) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { signIn, refreshAuth } = useAuth();

  // Check for password reset or invite tokens in URL (fallback if prop is not provided)
  const accessToken =
    searchParams.get("access_token") ||
    new URLSearchParams(window.location.hash.substring(1)).get("access_token");
  const refreshToken =
    searchParams.get("refresh_token") ||
    new URLSearchParams(window.location.hash.substring(1)).get("refresh_token");
  const type =
    searchParams.get("type") ||
    new URLSearchParams(window.location.hash.substring(1)).get("type");
  const token =
    searchParams.get("token") ||
    new URLSearchParams(window.location.hash.substring(1)).get("token");
  const provider =
    searchParams.get("provider") ||
    new URLSearchParams(window.location.hash.substring(1)).get("provider");

  // Only detect password reset if it's explicitly marked as recovery (with or without access_token)
  const detectedPasswordResetFlow = !!(
    (
      (token && type === "recovery" && !provider) || // Email reset link with token
      (accessToken && type === "recovery" && !provider)
    ) // Magic link reset with access_token
  );

  // Detect invite flow (type=invite or type=signup for new users)
  const detectedInviteFlow = !!(
    accessToken &&
    (type === "invite" || type === "signup" || type === "magiclink") &&
    !provider
  );

  // Use prop if provided, otherwise fall back to URL detection
  const isPasswordResetFlow =
    propIsPasswordResetFlow || detectedPasswordResetFlow;
  const isInviteFlow = propIsInviteFlow || detectedInviteFlow;

  // Determine initial mode
  const getInitialMode = () => {
    if (isPasswordResetFlow) return "update-password";
    if (isInviteFlow) return "create-password";
    return searchParams.get("mode") === "reset" ? "reset" : "signin";
  };

  const [mode, setMode] = useState<
    "signin" | "reset" | "update-password" | "create-password" | "invalid-link"
  >(getInitialMode());

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState<{
    email?: string;
    password?: string;
  }>({});
  const [pendingFieldErrors, setPendingFieldErrors] = useState<{
    email?: string;
    password?: string;
  } | null>(null);
  const mapRef = useRef<HTMLDivElement>(null);
  const isMountedRef = useRef(true);

  // Track component mount status
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Effect to handle pending field errors
  useEffect(() => {
    if (pendingFieldErrors) {
      console.log("Setting pending field errors:", pendingFieldErrors);
      setFieldErrors(pendingFieldErrors);
      setPendingFieldErrors(null);
    }
  }, [pendingFieldErrors]);

  // Handle OAuth callback - only implicit flow (direct tokens in URL hash)
  useEffect(() => {
    const handleOAuthCallback = async () => {
      // Handle implicit flow (direct tokens in URL hash) - but exclude password recovery and invite flows
      const isInviteType =
        type === "invite" || type === "signup" || type === "magiclink";
      if (accessToken && !token && type !== "recovery" && !isInviteType) {
        console.log("🔍 OAuth tokens detected in URL hash, processing...");
        setLoading(true);

        try {
          // Parse the token to get user information
          const tokenParts = accessToken.split(".");
          if (tokenParts.length !== 3) {
            throw new Error("Invalid JWT token format");
          }

          // Decode the payload (second part of JWT)
          const payload = JSON.parse(atob(tokenParts[1]));
          console.log("🔍 JWT payload:", payload);

          // Extract user data from JWT payload
          const user: User = {
            id: payload.sub,
            email: payload.email,
            user_metadata: payload.user_metadata || {},
            app_metadata: payload.app_metadata || {},
            aud: payload.aud,
            role: payload.role,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            email_confirmed_at: new Date().toISOString(),
            confirmed_at: new Date().toISOString(),
            last_sign_in_at: new Date().toISOString(),
            phone: undefined,
            is_anonymous: false,
          };

          // Create token data object
          const tokenData: StoredTokenData = {
            access_token: accessToken,
            refresh_token: refreshToken || "",
            expires_at: payload.exp || Math.floor(Date.now() / 1000) + 3600,
            user: user,
          };

          // Store the JWT token using existing token management
          const stored = await TokenManager.storeToken(tokenData);

          if (stored) {
            console.log(
              "✅ OAuth authentication successful, JWT token stored, user:",
              user.email
            );

            // Show success message
            toast.success(`Welcome back, ${user.email}!`, {
              duration: 3000,
              position: "bottom-center",
            });

            // Clear the URL parameters
            window.history.replaceState(
              {},
              document.title,
              window.location.pathname
            );

            // Refresh the auth context to pick up the newly stored tokens
            console.log("🔄 Refreshing auth context to pick up stored tokens");
            await refreshAuth();

            // Navigate to last active route or map as fallback
            const targetRoute = getLastActiveRoute();
            console.log(
              "🧭 Navigating to last active route after OAuth:",
              targetRoute
            );
            navigate(targetRoute, { replace: true });
            return;
          } else {
            throw new Error("Failed to store authentication token");
          }
        } catch (error) {
          console.error("OAuth token processing error:", error);
          const errorMessage =
            error instanceof Error
              ? error.message
              : "OAuth authentication failed";
          setError(errorMessage);
          toast.error(errorMessage, {
            duration: 5000,
            position: "bottom-center",
          });
        } finally {
          // Clear URL parameters even if OAuth failed
          window.history.replaceState(
            {},
            document.title,
            window.location.pathname
          );
          setLoading(false);
        }
      }
    };

    handleOAuthCallback();
  }, [navigate, accessToken, refreshToken, token, type, refreshAuth]);

  useEffect(() => {
    // Handle password reset flow
    if (isPasswordResetFlow) {
      // If we have actual recovery tokens in the URL, set the session AND store in localStorage
      if (accessToken && refreshToken && type === "recovery") {
        console.log("🔄 Processing password reset tokens...");

        // Set Supabase session for the password reset
        supabase.auth.setSession({
          access_token: accessToken!,
          refresh_token: refreshToken!,
        });

        // Also store in localStorage using our custom token management
        const storeRecoveryTokens = async () => {
          try {
            // Parse the token to get user information
            const tokenParts = accessToken.split(".");
            if (tokenParts.length === 3) {
              const payload = JSON.parse(atob(tokenParts[1]));

              // Extract user data from JWT payload
              const user: User = {
                id: payload.sub,
                email: payload.email,
                user_metadata: payload.user_metadata || {},
                app_metadata: payload.app_metadata || {},
                aud: payload.aud,
                role: payload.role,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
                email_confirmed_at: new Date().toISOString(),
                confirmed_at: new Date().toISOString(),
                last_sign_in_at: new Date().toISOString(),
                phone: undefined,
                is_anonymous: false,
              };

              // Create token data object
              const tokenData: StoredTokenData = {
                access_token: accessToken,
                refresh_token: refreshToken,
                expires_at: payload.exp || Math.floor(Date.now() / 1000) + 3600,
                user: user,
              };

              // Store the JWT token using existing token management
              await TokenManager.storeToken(tokenData);
              console.log("✅ Recovery tokens stored in localStorage");
            }
          } catch (error) {
            console.error("Failed to store recovery tokens:", error);
          }
        };

        storeRecoveryTokens();
      }
      setMode("update-password");
    } else if (isInviteFlow) {
      // Handle invite flow - set the session for password creation
      if (accessToken && refreshToken) {
        console.log("🔄 Processing invite tokens...");

        // Set Supabase session for the invite
        supabase.auth.setSession({
          access_token: accessToken!,
          refresh_token: refreshToken!,
        });

        // Store tokens for later use
        const storeInviteTokens = async () => {
          try {
            // Parse the token to get user information
            const tokenParts = accessToken.split(".");
            if (tokenParts.length === 3) {
              const payload = JSON.parse(atob(tokenParts[1]));
              console.log("📧 Invite for user:", payload.email);

              // Extract user data from JWT payload
              const user: User = {
                id: payload.sub,
                email: payload.email,
                user_metadata: payload.user_metadata || {},
                app_metadata: payload.app_metadata || {},
                aud: payload.aud,
                role: payload.role,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
                email_confirmed_at: new Date().toISOString(),
                confirmed_at: new Date().toISOString(),
                last_sign_in_at: new Date().toISOString(),
                phone: undefined,
                is_anonymous: false,
              };

              // Create token data object
              const tokenData: StoredTokenData = {
                access_token: accessToken,
                refresh_token: refreshToken,
                expires_at: payload.exp || Math.floor(Date.now() / 1000) + 3600,
                user: user,
              };

              // Store the JWT token using existing token management
              await TokenManager.storeToken(tokenData);
              console.log("✅ Invite tokens stored in localStorage");
            }
          } catch (error) {
            console.error("Failed to store invite tokens:", error);
          }
        };

        storeInviteTokens();
      }
      setMode("create-password");
    } else if (
      // Only show invalid link for actual invalid recovery attempts
      // Don't show for OAuth flows
      (token && type === "recovery" && !accessToken) ||
      (accessToken && type === "recovery" && !refreshToken)
    ) {
      // If we have partial recovery tokens but not complete, show invalid link
      setMode("invalid-link");
    }
  }, [
    searchParams,
    accessToken,
    refreshToken,
    isPasswordResetFlow,
    isInviteFlow,
    type,
    token,
  ]);

  // Initialize background map
  useEffect(() => {
    if (mapRef.current) {
      const mapStyle: StyleSpecification = {
        version: 8,
        sources: {
          osm: {
            type: "raster",
            tiles: ["https://tile.openstreetmap.org/{z}/{x}/{y}.png"],
            tileSize: 256,
            attribution: "© OpenStreetMap contributors",
          },
        },
        layers: [
          {
            id: "osm",
            type: "raster",
            source: "osm",
          },
        ],
      };

      // Create map with disabled interactions
      const map = new MapLibreMap({
        container: mapRef.current,
        style: mapStyle,
        center: [-74.006, 40.7128], // New York City coordinates [lng, lat]
        zoom: 12,
        attributionControl: false,
        interactive: false,
      });

      // Cleanup function
      return () => {
        map.remove();
      };
    }
  }, []);

  const handleAzureSignIn = async () => {
    setLoading(true);
    setError("");
    setMessage("");
    setFieldErrors({});

    try {
      // Use direct OAuth URL instead of Supabase client method
      const projectUrl = import.meta.env.VITE_SUPABASE_URL;
      const redirectUrl = `${window.location.origin}/`;

      const oauthUrl = `${projectUrl}/auth/v1/authorize?provider=azure&redirect_to=${encodeURIComponent(
        redirectUrl
      )}&scopes=email%20openid%20profile&prompt=select_account`;

      console.log("Redirecting to Azure OAuth:", oauthUrl);

      // Redirect to OAuth provider
      window.location.href = oauthUrl;
    } catch (error: unknown) {
      console.error("Azure sign-in error:", error);
      let errorMessage = "Failed to sign in with Azure";

      if (error instanceof Error) {
        errorMessage = error.message;
      }

      setError(errorMessage);
      toast.error(errorMessage, {
        duration: 5000,
        position: "bottom-center",
      });
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError("");
    setMessage("");
    setFieldErrors({}); // Clear any existing field errors

    try {
      if (mode === "signin") {
        const result = await signIn(email, password);

        if (result.success) {
          // Clear field errors on successful sign-in
          setFieldErrors({});

          // Don't set loading to false here - let the app redirect
        } else {
          // Handle failed sign-in directly without throwing
          console.log("=== SIGN IN FAILED - HANDLING ERROR ===");
          console.log("Sign in failed with error:", result.error);
          console.log("Component mounted check:", isMountedRef.current);

          // Check if component is still mounted before setting state
          if (!isMountedRef.current) {
            console.log(
              "Component unmounted during auth, not setting error state"
            );
            setLoading(false);
            return;
          }

          const errorMessage = result.error || "Sign in failed";
          console.log("Setting error message:", errorMessage);
          setError(errorMessage);
          toast.error(errorMessage);

          setLoading(false);
          console.log("Loading set to false, staying on auth page");
          return; // Don't continue to other modes
        }
      } else if (mode === "reset") {
        // Basic email validation before calling Supabase
        const isValidEmail = (value: string) =>
          /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

        if (!email || !isValidEmail(email)) {
          setFieldErrors({ email: "Please enter a valid email address" });
          throw new Error("Invalid email address");
        }

        // Prefer explicitly configured site URL, fallback to current origin
        const redirectBase =
          import.meta.env.VITE_PUBLIC_SITE_URL || window.location.origin;
        const redirectUrl = `${redirectBase}/reset-password`;

        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: redirectUrl,
        });
        if (error) {
          // Improve error clarity for common Supabase 400 causes
          const msg =
            typeof error.message === "string" ? error.message : String(error);

          // Redirect not allowed by Supabase settings
          if (
            msg.toLowerCase().includes("redirect") &&
            msg.toLowerCase().includes("allowed")
          ) {
            throw new Error(
              `Reset link redirect not allowed. Add ${redirectUrl} to Supabase Auth → URL Configuration → Redirect URLs.`
            );
          }

          // Missing or invalid email
          if (msg.toLowerCase().includes("email")) {
            throw new Error("Please provide a valid email to reset password.");
          }

          throw error;
        }

        setMessage("Password reset email sent! Check your inbox.");

        toast.success("Password reset email sent! Check your inbox.", {
          duration: 5000,
          position: "bottom-center",
        });
      } else if (mode === "update-password") {
        if (password !== confirmPassword) {
          setError("Passwords do not match");
          return;
        }

        if (password.length < 6) {
          setError("Password must be at least 6 characters long");
          return;
        }

        const { error } = await supabase.auth.updateUser({
          password: password,
        });

        if (error) throw error;

        // Show success toast
        toast.success("Password reset successfully", {
          duration: 5000,
          position: "bottom-center",
        });

        // Refresh auth context to pick up the stored tokens
        console.log("🔄 Refreshing auth context after password reset");
        await refreshAuth();

        // Give a moment for the auth context to update
        await new Promise((resolve) => setTimeout(resolve, 100));

        // Clear the password reset flow state in App.tsx
        if (onPasswordResetComplete) {
          onPasswordResetComplete();
        }

        // Clear the URL parameters
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname
        );

        // Navigate to last active route or map as fallback after password reset
        const targetRoute = getLastActiveRoute();
        console.log(
          "🧭 Navigating to last active route after password reset:",
          targetRoute
        );
        navigate(targetRoute, { replace: true });
        return;
      } else if (mode === "create-password") {
        // Handle invite flow - create password for new user
        if (password !== confirmPassword) {
          setError("Passwords do not match");
          return;
        }

        if (password.length < 6) {
          setError("Password must be at least 6 characters long");
          return;
        }

        console.log("🔐 Creating password for invited user...");

        const { error } = await supabase.auth.updateUser({
          password: password,
        });

        if (error) throw error;

        // Show success toast
        toast.success("Account created successfully! Welcome to Taskr.", {
          duration: 5000,
          position: "bottom-center",
        });

        // Refresh auth context to pick up the stored tokens
        console.log("🔄 Refreshing auth context after account creation");
        await refreshAuth();

        // Give a moment for the auth context to update
        await new Promise((resolve) => setTimeout(resolve, 100));

        // Clear the invite flow state in App.tsx
        if (onInviteComplete) {
          onInviteComplete();
        }

        // Clear the URL parameters
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname
        );

        // Navigate to map for new users
        console.log("🧭 Navigating to /map after account creation");
        navigate("/map", { replace: true });
        return;
      }
    } catch (error: unknown) {
      console.log("=== ERROR CAUGHT IN SIGN IN ===");
      console.log("Error object:", error);
      console.log("Current mode:", mode);

      let errorMessage = "An error occurred";

      if (error instanceof Error) {
        console.log("Error message:", error.message);
        // Handle specific authentication errors
        if (
          error.message.includes("Invalid login credentials") ||
          error.message.includes("AuthApiError")
        ) {
          errorMessage =
            "Invalid email or password. Please check your credentials and try again.";

          // Set field-specific errors for sign-in
          if (mode === "signin") {
            const errors = {
              email: "Please check your email address",
              password: "Please check your password",
            };
            console.log("Setting field errors:", errors);
            // Use pending approach to ensure state is set properly
            if (isMountedRef.current) {
              setPendingFieldErrors(errors);
            } else {
              console.log("Component unmounted, not setting field errors");
            }
          }
        } else if (error.message.includes("Email not confirmed")) {
          errorMessage =
            "Please check your email and click the confirmation link before signing in.";

          if (mode === "signin") {
            setFieldErrors({
              email: "Email address not confirmed",
            });
          }
        } else if (error.message.includes("Too many requests")) {
          errorMessage =
            "Too many login attempts. Please wait a moment and try again.";
        } else {
          errorMessage = error.message;
        }
      }

      console.log("Final error message:", errorMessage);

      // Show toast notification for immediate feedback
      toast.error(errorMessage, {
        duration: 5000,
        position: "bottom-center",
      });

      // Also set inline error for persistent display
      setError(errorMessage);
      console.log("Error state and fieldErrors set");
      console.log("Current fieldErrors after setting:", fieldErrors);
    } finally {
      setLoading(false);
    }
  };

  // // Show success state after password update
  // if (success) {

  //   return (
  //     <div className="flex flex-col justify-center items-center min-h-screen p-8 bg-slate-900 relative">
  //       {/* Background Map */}
  //       <div
  //         ref={mapRef}
  //         className="absolute z-10 top-0 left-0 h-screen w-screen"
  //       />
  //       <div className="relative z-10 w-full max-w-md bg-white rounded-lg shadow-lg p-8 text-center">
  //         <div className="text-green-600 text-5xl mb-4">✓</div>
  //         <h1 className="text-2xl font-bold text-gray-800 mb-4">
  //           Password Updated!
  //         </h1>
  //         <p className="text-gray-600 mb-4">
  //           Your password has been successfully updated.
  //         </p>
  //         <p className="text-sm text-gray-500">Redirecting to sign in...</p>
  //       </div>
  //     </div>
  //   );
  // }

  // Show invalid link state
  if (mode === "invalid-link") {
    return (
      <div className="flex flex-col justify-center items-center min-h-screen p-8 bg-slate-900 relative">
        {/* Background Map */}
        <div
          ref={mapRef}
          className="absolute z-10 top-0 left-0 h-screen w-screen"
        />
        <div className="relative z-10 w-full max-w-md bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="text-red-600 text-5xl mb-4">⚠️</div>
          <h1 className="text-2xl font-bold text-gray-800 mb-4">
            Invalid Reset Link
          </h1>
          <p className="text-gray-600 mb-6">
            Your password reset link is invalid or has expired. Please request a
            new password reset.
          </p>

          <button
            onClick={() => setMode("reset")}
            className="cursor-pointer w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors mb-3"
          >
            Request New Reset Link
          </button>

          <button
            onClick={() => setMode("signin")}
            className="cursor-pointer w-full bg-gray-200 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400 transition-colors"
          >
            Back to Sign In
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex justify-end min-h-screen relative overflow-visible">
      {/* Toast Notifications */}
      <Toaster
        position="bottom-center"
        toastOptions={{
          duration: 5000,
          style: {
            borderRadius: "10px",
            fontSize: "14px",
          },
          error: {
            style: {
              background: "#FEF2F2",
              color: "#B91C1C",
              border: "1px solid #FECACA",
            },
          },
          success: {
            style: {
              background: "#F0FDF4",
              color: "#166534",
              border: "1px solid #BBF7D0",
            },
          },
        }}
      />

      {/* Background Map */}
      <div
        ref={mapRef}
        className="absolute z-10 top-0 left-0 h-screen w-screen"
      />
      <div className="flex relative w-[504px] z-20 bg-white h-screen p-15">
        <div className="w-96 inline-flex flex-col justify-center items-center gap-5">
          {/* Logo */}
          <div className="w-28 h-10 relative">
            <img
              src="images/logo.png"
              alt="Taskr Logo"
              className="w-full h-full object-contain"
            />
          </div>

          {/* Main Content */}
          <div className="self-stretch flex flex-col justify-start items-start gap-6 bg-white rounded-2xl p-8">
            {/* Header */}
            <div className="self-stretch flex flex-col justify-start items-center gap-2">
              <div className="text-center justify-start text-2xl font-bold leading-9 custom-text-onyx">
                {mode === "signin" && "Sign in"}
                {mode === "reset" && "Forgot Password"}
                {mode === "update-password" && "Set New Password"}
                {mode === "create-password" && "Create Your Password"}
              </div>
              <div className="self-stretch text-center justify-start text-xs font-medium leading-none custom-text-gray-dark">
                {mode === "signin" &&
                  "Enter your email and password to access your account."}
                {mode === "reset" &&
                  "No worries. We'll send you reset instructions."}
                {mode === "update-password" &&
                  "Create a new password for your account."}
                {mode === "create-password" &&
                  "You've been invited! Create a password to complete your account setup."}
              </div>
            </div>

            {/* Form */}
            <div className="self-stretch flex flex-col justify-start items-center gap-5">
              <div className="self-stretch flex flex-col justify-start items-start gap-4">
                {mode !== "update-password" && mode !== "create-password" && (
                  <div className="self-stretch flex flex-col justify-start items-start gap-1.5">
                    <div className="inline-flex justify-start items-center gap-2">
                      <div className="justify-start text-sm font-semibold leading-tight custom-text-onyx">
                        Email
                      </div>
                    </div>
                    <div
                      className={`self-stretch px-3.5 py-3 rounded-[10px] inline-flex justify-start items-center ${
                        fieldErrors.email
                          ? "border-2 border-red-500 bg-red-50"
                          : "border border-transparent bg-gray-100"
                      }`}
                    >
                      <input
                        id="email"
                        type="email"
                        value={email}
                        onChange={(e) => {
                          setEmail(e.target.value);
                          // Clear email field error when user starts typing
                          if (fieldErrors.email) {
                            setFieldErrors((prev) => ({
                              ...prev,
                              email: undefined,
                            }));
                          }
                        }}
                        required
                        autoComplete="email"
                        className={`w-full text-xs font-medium leading-none focus:outline-none custom-text-onyx ${
                          fieldErrors.email ? "bg-red-50" : "bg-gray-100"
                        }`}
                        placeholder="Enter your email"
                      />
                    </div>
                    {fieldErrors.email && (
                      <div className="text-red-600 text-xs font-medium mt-1 px-1">
                        {fieldErrors.email}
                      </div>
                    )}
                  </div>
                )}

                {mode === "signin" && (
                  <div className="self-stretch flex flex-col justify-start items-start gap-1.5">
                    <div className="inline-flex justify-start items-center gap-2">
                      <div className="justify-start text-sm font-semibold leading-tight custom-text-onyx">
                        Password
                      </div>
                    </div>
                    <div
                      className={`self-stretch px-3.5 py-3 rounded-[10px] inline-flex justify-between items-center ${
                        fieldErrors.password
                          ? "border-2 border-red-500 bg-red-50"
                          : "border border-transparent bg-gray-100"
                      }`}
                    >
                      <input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        value={password}
                        onChange={(e) => {
                          setPassword(e.target.value);
                          // Clear password field error when user starts typing
                          if (fieldErrors.password) {
                            setFieldErrors((prev) => ({
                              ...prev,
                              password: undefined,
                            }));
                          }
                        }}
                        required
                        className={`flex-1 text-xs font-medium leading-none placeholder-text-gray focus:outline-none custom-text-onyx ${
                          fieldErrors.password ? "bg-red-50" : "bg-gray-100"
                        }`}
                        placeholder="Enter your password"
                        autoComplete="current-password"
                        minLength={6}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="cursor-pointer flex items-center justify-center w-4 h-4 hover:text-zinc-700 focus:outline-none custom-text-gray-dark"
                      >
                        {showPassword ? (
                          <AiOutlineEyeInvisible size={16} />
                        ) : (
                          <AiOutlineEye size={16} />
                        )}
                      </button>
                    </div>
                    {fieldErrors.password && (
                      <div className="text-red-600 text-xs font-medium mt-1 px-1">
                        {fieldErrors.password}
                      </div>
                    )}
                  </div>
                )}

                {mode === "update-password" && (
                  <>
                    {/* Hidden username field for accessibility */}
                    <input
                      type="email"
                      name="username"
                      autoComplete="username"
                      className="hidden"
                      tabIndex={-1}
                      readOnly
                    />

                    <div className="self-stretch flex flex-col justify-start items-start gap-1.5">
                      <div className="justify-start text-sm font-semibold leading-tight custom-text-onyx">
                        New Password
                      </div>
                      <div className="self-stretch px-3.5 py-3 bg-gray-100 rounded-[10px] inline-flex justify-start items-center">
                        <input
                          id="password"
                          type="password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          required
                          autoComplete="new-password"
                          className="w-full bg-transparent text-xs font-medium leading-none placeholder-text-gray focus:outline-none custom-text-onyx"
                          placeholder="Enter new password"
                          minLength={6}
                        />
                      </div>
                    </div>

                    <div className="self-stretch flex flex-col justify-start items-start gap-1.5">
                      <div className="justify-start text-sm font-semibold leading-tight custom-text-onyx">
                        Confirm Password
                      </div>
                      <div className="self-stretch px-3.5 py-3 bg-gray-100 rounded-[10px] inline-flex justify-start items-center">
                        <input
                          id="confirmPassword"
                          type="password"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          required
                          autoComplete="new-password"
                          className="w-full bg-transparent text-xs font-medium leading-none placeholder-text-gray focus:outline-none custom-text-onyx"
                          placeholder="Confirm new password"
                          minLength={6}
                        />
                      </div>
                    </div>
                  </>
                )}

                {mode === "create-password" && (
                  <>
                    {/* Hidden username field for accessibility */}
                    <input
                      type="email"
                      name="username"
                      autoComplete="username"
                      className="hidden"
                      tabIndex={-1}
                      readOnly
                    />

                    <div className="self-stretch flex flex-col justify-start items-start gap-1.5">
                      <div className="justify-start text-sm font-semibold leading-tight custom-text-onyx">
                        Password
                      </div>
                      <div className="self-stretch px-3.5 py-3 bg-gray-100 rounded-[10px] inline-flex justify-start items-center">
                        <input
                          id="password"
                          type="password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          required
                          autoComplete="new-password"
                          className="w-full bg-transparent text-xs font-medium leading-none placeholder-text-gray focus:outline-none custom-text-onyx"
                          placeholder="Create a password"
                          minLength={6}
                        />
                      </div>
                    </div>

                    <div className="self-stretch flex flex-col justify-start items-start gap-1.5">
                      <div className="justify-start text-sm font-semibold leading-tight custom-text-onyx">
                        Confirm Password
                      </div>
                      <div className="self-stretch px-3.5 py-3 bg-gray-100 rounded-[10px] inline-flex justify-start items-center">
                        <input
                          id="confirmPassword"
                          type="password"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          required
                          autoComplete="new-password"
                          className="w-full bg-transparent text-xs font-medium leading-none placeholder-text-gray focus:outline-none custom-text-onyx"
                          placeholder="Confirm your password"
                          minLength={6}
                        />
                      </div>
                    </div>
                  </>
                )}

                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={loading}
                  className="cursor-pointer self-stretch px-6 py-3 bg-indigo-800 rounded-[10px] inline-flex justify-center items-center gap-2.5 disabled:opacity-50 disabled:cursor-not-allowed transition-colors hover:bg-indigo-900"
                >
                  <div className="justify-start text-white text-sm font-semibold leading-tight">
                    {loading
                      ? "Loading..."
                      : mode === "signin"
                        ? "Sign In"
                        : mode === "update-password"
                          ? "Update Password"
                          : mode === "create-password"
                            ? "Create Account"
                            : "Send Reset Email"}
                  </div>
                </button>

                {/* Azure Sign In Button - Only show for signin mode */}
                {mode === "signin" && (
                  <>
                    <div className="self-stretch flex items-center gap-4">
                      <hr className="flex-1 border-gray-300" />
                      <span className="text-xs font-medium text-gray-500">
                        OR
                      </span>
                      <hr className="flex-1 border-gray-300" />
                    </div>

                    <button
                      type="button"
                      onClick={handleAzureSignIn}
                      disabled={loading}
                      className="cursor-pointer self-stretch px-6 py-3 bg-white border border-gray-300 rounded-[10px] inline-flex justify-center items-center gap-2.5 disabled:opacity-50 disabled:cursor-not-allowed transition-colors hover:bg-gray-50"
                    >
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 23 23"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="shrink-0"
                      >
                        <path
                          d="M1.194 10.708h9.1v-9.1H1.194v9.1zm10.708-9.1v9.1h9.1v-9.1h-9.1zm-10.708 21.8h9.1v-9.1H1.194v9.1zm10.708 0h9.1v-9.1h-9.1v9.1z"
                          fill="#00BCF2"
                        />
                      </svg>
                      <div className="justify-start text-gray-700 text-sm font-semibold leading-tight">
                        Sign in with Microsoft
                      </div>
                    </button>
                  </>
                )}
              </div>

              {mode === "signin" && (
                <button
                  type="button"
                  onClick={() => setMode("reset")}
                  className="cursor-pointer text-center justify-start text-indigo-800 text-xs font-bold leading-none hover:underline"
                >
                  Forgot your password?
                </button>
              )}
            </div>

            {error && (
              <div className="self-stretch p-3 bg-red-100 border border-red-400 text-red-700 rounded-[10px] text-xs font-medium ">
                {error}
              </div>
            )}

            {message && (
              <div className="self-stretch p-3 bg-green-100 border border-green-400 text-green-700 rounded-[10px] text-xs font-medium ">
                {message}
              </div>
            )}
          </div>

          {/* Footer Links */}
          <div className="inline-flex justify-start items-center gap-2">
            {mode === "reset" && (
              <>
                <div className="text-center justify-start text-xs font-semibold leading-none custom-text-gray-dark">
                  Remember your password?
                </div>
                <button
                  type="button"
                  onClick={() => setMode("signin")}
                  className="cursor-pointer text-center justify-start text-indigo-800 text-xs font-bold leading-none hover:underline"
                >
                  Sign in
                </button>
              </>
            )}
          </div>
        </div>
        {/* Version Info */}
        <div className="absolute bottom-4 left-4 text-sm text-gray-600 bg-white/90 px-2 py-1 rounded shadow-sm z-99999">
          Software: {getDisplayVersion()}
        </div>
      </div>
    </div>
  );
}
